import { useCallback, useEffect, useState } from 'react'
import { conversationsApi, type ChannelCatalogEntry } from '@/services/conversationsApi'
import { readPinnedChannelKeys, writePinnedChannelKeys } from '@/lib/channel-inbox'

function dedupeCatalog(entries: ChannelCatalogEntry[]): ChannelCatalogEntry[] {
  const byKey = new Map<string, ChannelCatalogEntry>()
  for (const entry of entries) {
    const key = String(entry?.key || '').trim()
    if (!key) continue
    const lowered = key.toLowerCase()
    const existing = byKey.get(lowered)
    if (!existing) {
      byKey.set(lowered, entry)
      continue
    }
    // Prefer system entry over custom when there is a case-only collision.
    if (entry.is_system && !existing.is_system) {
      byKey.set(lowered, entry)
    }
  }
  return Array.from(byKey.values())
}

export function useInboxChannelCatalog() {
  const [catalog, setCatalog] = useState<ChannelCatalogEntry[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pinnedKeys, setPinnedKeys] = useState<string[]>(() => readPinnedChannelKeys())

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const res = await conversationsApi.getChannelCatalog()
        if (!cancelled) {
          setCatalog(Array.isArray(res.channels) ? dedupeCatalog(res.channels) : [])
          setError(null)
        }
      } catch (e) {
        if (!cancelled) {
          setCatalog(null)
          setError(e instanceof Error ? e.message : 'Failed to load channel catalog')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const setPinnedKeysPersisted = useCallback((keys: string[]) => {
    setPinnedKeys(keys)
    writePinnedChannelKeys(keys)
  }, [])

  const togglePinChannelKey = useCallback((key: string) => {
    setPinnedKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      writePinnedChannelKeys(next)
      return next
    })
  }, [])

  return {
    catalog,
    loading,
    error,
    pinnedKeys,
    setPinnedKeys: setPinnedKeysPersisted,
    togglePinChannelKey,
  }
}
